import { Component, EventEmitter, inject, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SignInRequest } from '../../../../../models/users/requests/sign-in-request';
import { LoadingOptions } from '../../../../../models/shared/loading-options';
import { BaseComponent } from '../../../../../models/base/base-component';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PortalService } from '../../../../../services/portal/portal.service';
import { SignInViewModel } from '../../../viewModels/sign-in-view-model';
import { PortalType } from '../../../../../models/enum/shared/portal-type';
import { BaseButtonSizeEnum } from '../../../../shared/components/base-button/base-button-size.enum';
import { Routes } from 'src/app/routes/routes';
import { createSignInFormGroup } from '../../../forms/sign-in-form-group';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss', '../auth.component.scss']
})
export class SignInComponent extends BaseComponent {
  private viewModel = inject(SignInViewModel);
  private portalService = inject(PortalService);
  private route = inject(ActivatedRoute);

  @Output() loadingOpts = new EventEmitter<LoadingOptions>();

  constructor(private router: Router) {
    super();
  }

  // View
  private _loadingOpts = new BehaviorSubject(LoadingOptions.default());
  public loadingOpts$ = this._loadingOpts as Observable<LoadingOptions>;
  public isLoading$ = this._loadingOpts.asObservable().pipe(map(opts => opts.isLoading));
  // Variables
  public formGroup = createSignInFormGroup();
  public formSubmitted = false;
  public loginTitle$ = this.viewModel.loginTitle$;
  public isMemberPortal$ = this.portalService.isMember$;
  public isEmployerPortal$ = this.portalService.isEmployer$;

  setupBindings() {
    this.viewModel.loadingOpts$.subscribeWhileAlive({
      owner: this,
      next: opts => this.loadingOpts.emit(opts)
    });
  }

  // Prefill runs in setupViews (ngOnInit) so synchronous emissions patch the form before the first change detection pass
  setupViews() {
    this.bindUrlParams();
    this.viewModel.preFillEmail$.subscribeWhileAlive({
      owner: this,
      next: email => {
        if (!!email) {
          this.formGroup.controls.email.patchValue(email);
        }
      }
    });
  }

  bindUrlParams() {
    this.viewModel.getUrlParams(this.route);
  }

  // View Actions

  public submit(): void {
    this.formSubmitted = true;
    if (this.formGroup.invalid) {
      return;
    }
    const { email, password } = this.formGroup.getRawValue();
    const req = new SignInRequest(email, password);
    this.portalService.portalType$.once(type => {
      switch (type) {
        case PortalType.Internal:
          this.viewModel.internalUserSignIn(req);
          break;
        case PortalType.Member:
          this.viewModel.memberSignIn(req);
          break;
        case PortalType.Employer:
          this.viewModel.employerSignIn(req);
          break;
      }
    });
  }

  public navigateToForgotPassword() {
    const tempEmail = this.formGroup.controls.email.value;
    this.router.navigate(['auth/forgot-password'], { queryParams: { email: tempEmail } }).then();
  }

  public goToMemberSelfEnrolment(): void {
    this.router.navigate([Routes.mpSelfEnrolment()]).then();
  }

  public goToEmployerSelfEnrolment(): void {
    this.router.navigate([Routes.epSelfEnrolment()]).then();
  }

  protected readonly BaseButtonSizeEnum = BaseButtonSizeEnum;
}
